import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiBook, FiCheck, FiClock, FiLock, FiPlay, FiZap } from 'react-icons/fi';
import './LessonPlayer.css';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { getAcademyLessonBySlug } from './catalog';

const safeOptions = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return null;
};

const LessonPlayer = () => {
  const { courseId, lessonSlug } = useParams();
  const { isAuthenticated } = useAuth();

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [steps, setSteps] = useState([]);

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(1);
  const [currentStep, setCurrentStep] = useState(null);
  const [pendingNextStep, setPendingNextStep] = useState(null);
  const [pendingNextStepIndex, setPendingNextStepIndex] = useState(null);

  const [answerText, setAnswerText] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null); // { correct, explanation }
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setFeedback(null);
        setPendingNextStep(null);
        setPendingNextStepIndex(null);
        setAnswerText('');
        setSelectedOption(null);

        const { data } = await academyAPI.getLesson(courseId, lessonSlug);
        setCourse(data.course);
        setLesson(data.lesson);
        setSteps(Array.isArray(data.steps) ? data.steps : []);

        const initialCompleted = !!data.progress?.completed;
        const initialStep = Number(data.progress?.current_step || 1);
        const initialStarted = !!data.progress;
        const initialXp = Number(data.progress?.xp_earned || 0);

        setCompleted(initialCompleted);
        setStarted(initialStarted);
        setCurrentStepIndex(initialStep);
        setXpEarned(initialXp);

        const stepObj = (Array.isArray(data.steps) ? data.steps : []).find(
          (s) => Number(s.step_index) === initialStep
        );
        setCurrentStep(stepObj || null);
      } catch (e) {
        console.error('Error loading lesson:', e);

        // Fallback: allow viewing lesson content from local catalog
        const resolved = getAcademyLessonBySlug(courseId, lessonSlug);
        if (resolved) {
          const { course: c, lesson: l } = resolved;
          const stepList = Array.isArray(l.steps) ? l.steps : [];
          setCourse({ id: c.slug, slug: c.slug, title: c.title, totalLessons: 0 });
          setLesson({ id: l.slug, slug: l.slug, title: l.title, type: 'guided', duration: l.duration, xp: l.xp });
          setSteps(stepList);
          setCompleted(false);
          setStarted(false);
          setCurrentStepIndex(1);
          setXpEarned(0);
          setCurrentStep(stepList.find((s) => Number(s.step_index) === 1) || null);
        } else {
          setError('No se pudo cargar la lección');
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, lessonSlug, isAuthenticated]);

  const stepOptions = useMemo(() => safeOptions(currentStep?.options), [currentStep]);

  const progressPercent = useMemo(() => {
    const total = Math.max(1, steps.length || 1);
    if (completed) return 100;
    const idx = Math.max(1, Number(currentStepIndex || 1));
    return Math.max(0, Math.min(100, ((idx - 1) / total) * 100));
  }, [completed, currentStepIndex, steps.length]);

  const handleStart = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      const { data } = await academyAPI.startLesson(courseId, lessonSlug);
      setStarted(true);
      setCompleted(false);
      setFeedback(null);
      setPendingNextStep(null);
      setPendingNextStepIndex(null);
      setAnswerText('');
      setSelectedOption(null);
      setCurrentStepIndex(Number(data.currentStep || 1));
      setCurrentStep(data.step || null);
    } catch (e) {
      console.error('Error starting lesson:', e);
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    const answer = stepOptions ? selectedOption : answerText;
    if (!answer || String(answer).trim().length === 0) return;

    try {
      const { data } = await academyAPI.answerStep(courseId, lessonSlug, {
        stepIndex: currentStepIndex,
        answer
      });

      setFeedback({ correct: !!data.correct, explanation: data.explanation || null });
      setXpEarned((prev) => prev + Number(data.xpDelta || 0));

      if (!data.correct) {
        return;
      }

      if (data.completed) {
        setCompleted(true);
        setPendingNextStep(null);
        setPendingNextStepIndex(null);
        return;
      }

      if (data.nextStep && data.nextStepIndex) {
        setPendingNextStep(data.nextStep);
        setPendingNextStepIndex(Number(data.nextStepIndex));
        setAnswerText('');
        setSelectedOption(null);
      }
    } catch (e) {
      console.error('Error submitting answer:', e);
    }
  };

  const handleContinue = () => {
    if (!pendingNextStep || !pendingNextStepIndex) return;
    setCurrentStepIndex(pendingNextStepIndex);
    setCurrentStep(pendingNextStep);
    setPendingNextStep(null);
    setPendingNextStepIndex(null);
    setFeedback(null);
    setAnswerText('');
    setSelectedOption(null);
  };

  if (loading) {
    return (
      <div className="lesson-loading">
        <div className="loading-animation">📚</div>
        <p>Preparando lección...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lesson-loading">
        <p>{error}</p>
        <Link to={`/academy/course/${courseId}`} className="back-btn">
          <FiArrowLeft />
        </Link>
      </div>
    );
  }

  return (
    <div className="lesson-player">
      {!isAuthenticated && showLoginPrompt && (
        <LoginPrompt
          message="Inicia sesión para iniciar y guardar tu progreso"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      <header className="lesson-header">
        <Link to={`/academy/course/${courseId}`} className="back-btn">
          <FiArrowLeft />
        </Link>

        <div className="lesson-progress-bar">
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>

        <div className="lesson-stats">
          <div className="xp-counter">
            <FiZap /> {xpEarned} XP
          </div>
        </div>
      </header>

      <main className="lesson-content">
        {!started && !completed && (
          <div className="intro-phase">
            <div className="intro-icon">📖</div>
            <h1>{lesson?.title}</h1>
            {course?.title && (
              <p className="intro-description">
                Curso: {course.title}
              </p>
            )}
            <div className="lesson-meta">
              <span><FiClock /> {lesson?.duration} minutos</span>
              <span><FiZap /> +{lesson?.xp} XP</span>
            </div>
            <p className="intro-description">Lección guiada paso a paso.</p>
            <button className="btn-start" onClick={handleStart}>
              {isAuthenticated ? (<><FiPlay /> Comenzar</>) : (<><FiLock /> Inicia sesión</>)}
            </button>
          </div>
        )}

        {completed && (
          <div className="intro-phase">
            <div className="intro-icon">✅</div>
            <h1>Lección completada</h1>
            <p className="intro-description">
              Has completado: {lesson?.title}
            </p>
            <Link to={`/academy/course/${courseId}`} className="btn-start">
              <FiBook /> Volver al curso
            </Link>
          </div>
        )}

        {started && !completed && currentStep && (
          <div className="practice-phase">
            <h1>{lesson?.title}</h1>
            <div className="lesson-meta">
              <span>Paso {currentStepIndex}/{Math.max(1, steps.length)}</span>
              <span><FiZap /> +{Number(currentStep.xp || 0)} XP</span>
            </div>

            <div className="practice-card">
              <p style={{ whiteSpace: 'pre-wrap' }}>{currentStep.prompt}</p>

              {stepOptions ? (
                <div className="answer-options">
                  {stepOptions.map((opt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`option-btn ${selectedOption === opt ? 'selected' : ''}`}
                      onClick={() => setSelectedOption(opt)}
                      disabled={!!pendingNextStep}
                    >
                      {String(opt)}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  type="text"
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  placeholder="Escribe tu respuesta"
                  disabled={!!pendingNextStep}
                  style={{ width: '100%' }}
                />
              )}

              {feedback && (
                <div className={`result-card ${feedback.correct ? 'correct' : 'incorrect'}`}>
                  <div className="result-icon">{feedback.correct ? '✅' : '❌'}</div>
                  {feedback.explanation && <p>{feedback.explanation}</p>}
                </div>
              )}

              <div className="practice-actions">
                {pendingNextStep ? (
                  <button className="btn-continue" onClick={handleContinue}>
                    Continuar
                  </button>
                ) : (
                  <button
                    className="btn-check"
                    onClick={handleSubmit}
                    disabled={stepOptions ? !selectedOption : !answerText.trim()}
                  >
                    <FiCheck /> Comprobar
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LessonPlayer;
